import React, { useState, useEffect, useMemo } from 'react';
import './App.css';

// Helpers to get YouTube embed url
function getYouTubeId(url = '') {
  if (!url) return null;
  const str = String(url).trim();
  const patterns = [
    /youtu\.be\/([\w-]{11})/i,
    /[?&]v=([\w-]{11})/i,
    /\/embed\/([\w-]{11})/i,
    /\/shorts\/([\w-]{11})/i,
  ];
  for (const p of patterns) {
    const m = str.match(p);
    if (m) return m[1];
  }
  const m2 = str.match(/([\w-]{11})(?:[?&#]|$)/);
  return m2 ? m2[1] : null;
}
function getYouTubeEmbedUrl(url) {
  const id = getYouTubeId(url);
  return id ? `https://www.youtube.com/embed/${id}?rel=0` : null;
}

function App() {
  const getInitialTheme = () => {
    if (typeof window === 'undefined') return 'light';
    const stored = localStorage.getItem('theme');
    if (stored) return stored;
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };
  const [theme, setTheme] = useState(getInitialTheme);

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // NEW: search & filters state
  const [query, setQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all'); // all | 1 | 2 | 3
  const [priceFilter, setPriceFilter] = useState('all'); // all | free | paid
  // NEW: category filter
  const [categoryFilter, setCategoryFilter] = useState('all'); // all | __none | <category>

  // NEW: Build categories list for dropdown (includes "Chưa phân loại" when applicable)
  const categories = useMemo(() => {
    const set = new Set();
    let hasNone = false;
    for (const c of courses) {
      const v = c && c.category != null ? String(c.category).trim() : '';
      if (v) set.add(v);
      else hasNone = true;
    }
    const arr = Array.from(set).sort((a, b) => a.localeCompare(b, 'vi'));
    if (hasNone) arr.unshift('Chưa phân loại');
    return arr;
  }, [courses]);

  // Derived: filtered courses
  const filteredCourses = useMemo(() => {
    const q = query.trim().toLowerCase();
    return courses.filter((c) => {
      // text match on subject, instructor, tags
      const hay = [c.subject, c.user, Array.isArray(c.tags) ? c.tags.join(' ') : '']
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (q && !hay.includes(q)) return false;

      // level filter
      if (levelFilter !== 'all') {
        const lvl = Number(levelFilter);
        if (Number(c.level) !== lvl) return false;
      }

      // price filter
      if (priceFilter !== 'all') {
        const isPaid = Number(c.price) > 0;
        if (priceFilter === 'free' && isPaid) return false;
        if (priceFilter === 'paid' && !isPaid) return false;
      }

      // category filter
      if (categoryFilter !== 'all') {
        const val = c && c.category != null ? String(c.category).trim() : '';
        if (categoryFilter === '__none') {
          if (val) return false; // want only items without category
        } else {
          if (val.toLowerCase() !== categoryFilter.toLowerCase()) return false;
        }
      }

      return true;
    });
  }, [courses, query, levelFilter, priceFilter, categoryFilter]);

  useEffect(() => {
    // Apply theme to body and persist
    document.body.classList.toggle('dark', theme === 'dark');
    try { localStorage.setItem('theme', theme); } catch {}
  }, [theme]);

  useEffect(() => {
    fetchCourses();
  }, []);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  // Đọc từ public/courses.json để tránh CORS
  const fetchCourses = async () => {
    try {
      const resp = await fetch(process.env.PUBLIC_URL + '/courses.json', { cache: 'no-store' });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const json = await resp.json();
      const data = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
      setCourses(data);
      setLoading(false);
    } catch (err) {
      setError('Không thể tải dữ liệu khóa học');
      setLoading(false);
      console.error('Error fetching courses:', err);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Đang tải khóa học...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <h2>❌ Lỗi</h2>
        <p>{error}</p>
        <button onClick={fetchCourses}>Thử lại</button>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="header">
        {/* Theme toggle */}
        <button
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label="Chuyển chế độ sáng/tối"
          title="Chuyển chế độ sáng/tối"
        >
          {theme === 'dark' ? '🌙 Tối' : '☀️ Sáng'}
        </button>
        {/* Logo */}
        <img
          className="logo"
          src="https://app.aistudybuddy.net//logo.png"
          alt="AI Study Buddy"
          loading="lazy"
        />
        <h1>🎓 AI Study Buddy - Khoá học Online</h1>
      </header>

      <main className="main">
        {/* Filters toolbar */}
        <div className="filters">
          <div className="filters-row">
            <input
              type="text"
              className="filter-input"
              placeholder="🔎 Tìm kiếm theo tên khoá, giảng viên, tag..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select
              className="filter-select"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              aria-label="Lọc theo cấp độ"
            >
              <option value="all">Cấp độ: Tất cả</option>
              <option value="1">Cơ bản</option>
              <option value="2">Trung bình</option>
              <option value="3">Nâng cao</option>
            </select>
            <select
              className="filter-select"
              value={priceFilter}
              onChange={(e) => setPriceFilter(e.target.value)}
              aria-label="Lọc theo giá"
            >
              <option value="all">Giá: Tất cả</option>
              <option value="free">Miễn phí</option>
              <option value="paid">Trả phí</option>
            </select>
            {/* NEW: Category filter */}
            <select
              className="filter-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              aria-label="Lọc theo danh mục"
            >
              <option value="all">Danh mục: Tất cả</option>
              {categories.map((cat) => (
                <option key={cat} value={cat === 'Chưa phân loại' ? '__none' : cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div className="filters-count">{filteredCourses.length} khoá học</div>
        </div>

        <div className="courses-grid">
          {filteredCourses.map((course, index) => (
            <CourseCard key={index} course={course} />
          ))}
        </div>
      </main>
    </div>
  );
}

function CourseCard({ course }) {
  const createSlug = (subject) => {
    return subject
      .toLowerCase()
      .replace(/[àáạảãâầấậẩẫăằắặẳẵ]/g, 'a')
      .replace(/[èéẹẻẽêềếệểễ]/g, 'e')
      .replace(/[ìíịỉĩ]/g, 'i')
      .replace(/[òóọỏõôồốộổỗơờớợởỡ]/g, 'o')
      .replace(/[ùúụủũưừứựửữ]/g, 'u')
      .replace(/[ỳýỵỷỹ]/g, 'y')
      .replace(/đ/g, 'd')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  };

  const slug = course.slug || createSlug(course.subject);
  const metaDescription = course.first_day_content 
    ? course.first_day_content.substring(0, 150) + '...'
    : 'Khóa học chất lượng cao tại AI Study Buddy';
  const embedUrl = getYouTubeEmbedUrl(course.demo_video_url);
  // NEW: category display with fallback
  const category = (course.category && String(course.category).trim()) || 'Chưa phân loại';

  return (
    <div className="course-card">
      <div className="course-header">
        <div className="course-instructor">👨‍🏫 {course.user}</div>
        <h3 className="course-title">
          <a href={`/${slug}.html`} title="Xem chi tiết khóa học">
            {course.subject}
          </a>
        </h3>
        <div className="course-slug">📄 {slug}.html</div>
      </div>

      <div className="course-meta">
        <span className="meta-item">📂 {category}</span>
        <span className="meta-item">⏱️ {course.hours_per_day}h/ngày</span>
        <span className="meta-item">📅 {course.total_day} ngày</span>
        <span className="meta-item">
          📈 {course.level === 1 ? 'Cơ bản' : course.level === 2 ? 'Trung bình' : 'Nâng cao'}
        </span>
        {course.price > 0 && (
          <span className="meta-item">💰 {course.discount_price || course.price} VND</span>
        )}
      </div>

      {embedUrl && (
        <div className="video-responsive">
          <iframe
            src={embedUrl}
            title={`${course.subject} - Video demo`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
          />
        </div>
      )}

      <div className="course-description">{metaDescription}</div>

      {course.tags && course.tags.length > 0 && (
        <div className="course-tags">
          {course.tags.map((tag, index) => (
            <span key={index} className="tag">{tag}</span>
          ))}
        </div>
      )}

      <div className="course-actions">
        {course.demo_video_url && (
          <a href={course.demo_video_url} target="_blank" rel="noopener noreferrer" className="video-link">
            📹 Video Demo (mở YouTube)
          </a>
        )}
        <a href={`/${slug}.html`} className="details-link">🔎 Xem chi tiết</a>
        <div className="seo-info">
          <small>🔍 SEO: Slug được tạo tự động</small>
        </div>
      </div>
    </div>
  );
}

export default App;
